import { CommonModule } from '@angular/common';
import { Component, Injector, OnInit, OnDestroy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule, Router } from '@angular/router';
import {DEBOUNCE_TIME, extractErrorMessage, goToNextPage} from 'src/app/common/common';
import { ChannelViewModel } from 'src/app/model/channel.model';
import { AuthService } from 'src/app/services/auth.service';
import {ApiService, CustomHttpParams} from 'src/app/services/api.service';
import { NgxPaginationModule } from 'ngx-pagination';
import {PaginationClass, PaginationModel} from "../../../../common/pagination";
import {LoaderComponent} from "../../../common/loader/loader.component";
import { BehaviorSubject, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-channel-index',
  standalone: true,
  imports: [RouterModule, CommonModule, FormsModule, NgxPaginationModule, LoaderComponent],
  templateUrl: './channel-index.component.html',
  styleUrl: './channel-index.component.scss'
})
export class ChannelIndexComponent implements OnInit, OnDestroy {
  // Search fields for columns (excluding complex nested ones)
  protected channelIdSearch: string = '';
  protected channelNameSearch: string = '';

  // Debounce search using BehaviorSubject
  private searchParams$ = new BehaviorSubject<any>({
    channelId: '',
    channelName: '',
  });
  private unsubscribe = new Subject<void>();

  protected readonly paginationId = 'channelsPagination';
  private pagination = new PaginationClass(this.paginationId);
  protected paginationModel: PaginationModel = this.pagination.getPaginationModel();

  public isAdmin: boolean = false;
  public isLoading: boolean = false;
  public displayedChannels: ChannelViewModel[] = [];
  public error: string | null = null;
  public router: Router;

  constructor(
    private injector: Injector,
    private authService: AuthService,
    private channelService: ApiService
  ) {
    this.router = injector.get(Router);
  }

  ngOnInit(): void {
    this.isAdmin = this.authService.isAdmin();
    this.paginationModel = this.pagination.resetPagination();

    // Setup debounced search that tracks actual parameter changes
    this.searchParams$.pipe(
      debounceTime(DEBOUNCE_TIME),
      distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
      takeUntil(this.unsubscribe)
    ).subscribe(() => {
      this.loadChannels();
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  buildSearchParams(): CustomHttpParams {
    const params: CustomHttpParams = {
      'depth': 3,
      'limit': this.pagination.itemsPerPage,
      'offset': this.pagination.offset,
    };

    // Add search parameters if they exist
    if (this.channelIdSearch) {
      params['id'] = this.channelIdSearch.trim();
    }
    if (this.channelNameSearch) {
      params['display_label'] = this.channelNameSearch.trim();
    }
    return params;
  }

  triggerSearch() {
    this.paginationModel = this.pagination.resetPagination();

    // Update the search parameters subject with current values
    this.searchParams$.next({
      channelId: this.channelIdSearch,
      channelName: this.channelNameSearch,
    });
  }

  loadChannels() {
    this.isLoading = true;
    this.error = null;

    // Build parameters using the centralized method
    const queryParams = this.buildSearchParams();

    this.channelService.getChannel(queryParams)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: (response: any) => {
          this.displayedChannels = response.map((chan: ChannelViewModel) => chan);
          this.paginationModel = this.pagination.updateTotalItems(response.length);
          this.isLoading = false;
        },
        error: (err) => {
          console.error('Error loading channels:', err);
          this.error = extractErrorMessage(err, "Failed to load channels.");
          this.isLoading = false;
        }
      });
  }

  handlePageChange(newPage: number) {
    this.paginationModel = this.pagination.changePage(newPage);
    this.loadChannels();
  }

  nextPage(event: any, url: string, router: Router) {
    return goToNextPage(event, url, router);
  }

  delete(channel: any) {
    if (confirm('Are you sure you want to delete this channel?')) {
      this.channelService.deleteChannel(channel.id)
        .pipe(takeUntil(this.unsubscribe))
        .subscribe({
          next: () => {
            this.loadChannels(); // Reload after successful deletion
          },
          error: (err) => {
            console.error('Error deleting channel:', err);
            this.error = extractErrorMessage(err, "Failed to delete channel.");
          }
        });
    }
  }
}
